import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Item as AccordionItem, Content, Trigger } from '@radix-ui/react-accordion';
import clsx from 'clsx';
import ScrollLock from 'react-scrolllock';
import { Drawer } from 'vaul';
import {
  faAngleDown,
  faSlidersSimple,
} from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { GENRE_KEYS, Genres } from '@soundxyz/utils/const';
import { theme } from '@components-shared/src/stitches';
import { useSingleLocationSelect } from '@components/Location/select';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ParamsStore, useParams } from '@context/params';
import { ReleaseStatus } from '@graphql/reactQuery';
import { AVAILABILITY } from './constants';

export const MobileFilters = ({
  showAvailabilityFilter,
  showGenreFilter,
  showLocationFilter,
}: {
  showAvailabilityFilter: boolean;
  showGenreFilter: boolean;
  showLocationFilter: boolean;
}) => {
  const { search_availability, genre, search_location_label, search_location_place_id } =
    useParams();

  const initialLocation = (() => {
    if (search_location_label && search_location_place_id) {
      return {
        label: search_location_label,
        placeId: search_location_place_id,
      };
    }
    return null;
  })();

  const [isKeyboardOpen, setIsKeyboardOpen] = useState<boolean>(false);

  const { locationSelect, locationValue } = useSingleLocationSelect({
    initialLocation: initialLocation,
    placeholder: 'Location',
    setIsKeyboardOpen,
    onLocationChange: value => {
      if (value) {
        ParamsStore.search_location_label = value.label;
        ParamsStore.search_location_place_id = value.value.place_id;
      } else {
        ParamsStore.search_location_label = null;
        ParamsStore.search_location_place_id = null;
      }
    },
  });

  const filterParams = [!!genre, !!search_location_label, !!search_availability];

  const numFiltersApplied = filterParams.filter(Boolean).length;

  return (
    <Drawer.Root>
      <div className="mr-4 mt-2">
        <Drawer.Trigger asChild>
          <div className="relative w-4">
            <IconButton
              icon={
                <FontAwesomeIcon
                  icon={faSlidersSimple}
                  color={theme.colors.neutral800.toString()}
                />
              }
            />
            {numFiltersApplied > 0 && (
              <div className="absolute bottom-3.5 left-3 flex h-[20px] w-[20px] items-center justify-center rounded-full border-2 border-white bg-base800">
                <span className="font-base text-base-s font-medium text-white ">
                  {numFiltersApplied}
                </span>
              </div>
            )}
          </div>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/40" />
          <Drawer.Content
            className={clsx(
              'bg-gray-100 mt-24 flex h-full flex-col rounded-t-[10px]',
              isKeyboardOpen ? 'max-h-[86%]' : 'max-h-[64%]',
              'fixed bottom-0 left-0 right-0 z-mobileNav',
            )}
          >
            <div className="flex-1 rounded-t-[10px] bg-white p-4">
              <div className="mx-auto mb-8 h-1 w-10 flex-shrink-0 rounded-full bg-base300" />
              <div className="mx-auto max-w-md">
                {/* To improve UX we want to open all items by default */}
                <Accordion type="multiple" defaultValue={['availability', 'genre', 'location']}>
                  {showAvailabilityFilter && (
                    <AccordionItem
                      value="availability"
                      className="mb-5 data-[state=closed]:animate-closeAccordion data-[state=open]:animate-openAccordion"
                    >
                      <Trigger className="group flex w-full items-center justify-between">
                        <div className="mb-2 font-title text-title-m font-medium">
                          Availability
                          {!!search_availability && (
                            <span className="font-title text-title-m font-normal"> (1)</span>
                          )}
                        </div>
                        <div className="transition-transform group-data-[state=open]:rotate-180">
                          <FontAwesomeIcon icon={faAngleDown} />
                        </div>
                      </Trigger>
                      <Content className="flex gap-2">
                        {Object.values(ReleaseStatus).map((value, index) => {
                          const isAvailabilitySelected = value === search_availability;

                          return (
                            <Chip
                              key={index}
                              text={AVAILABILITY[value]}
                              isSelected={isAvailabilitySelected}
                              onClick={() => {
                                if (isAvailabilitySelected || !value)
                                  return (ParamsStore.search_availability = null);

                                return (ParamsStore.search_availability = value);
                              }}
                            />
                          );
                        })}
                      </Content>
                    </AccordionItem>
                  )}
                  {showGenreFilter && (
                    <AccordionItem
                      value="genre"
                      className="mb-5 data-[state=closed]:animate-closeAccordion data-[state=open]:animate-openAccordion"
                    >
                      <Trigger className="group flex w-full items-center justify-between">
                        <div className="mb-2 font-title text-title-m font-medium">
                          Genres
                          {!!genre && (
                            <span className="font-title text-title-m font-normal"> (1)</span>
                          )}
                        </div>
                        <div className="transition-transform group-data-[state=open]:rotate-180">
                          <FontAwesomeIcon icon={faAngleDown} />
                        </div>
                      </Trigger>
                      <ScrollLock>
                        <Content className="grid h-[160px] grid-cols-2 gap-2 overflow-auto">
                          {GENRE_KEYS.map(value => {
                            const isGenreSelected = value === genre;

                            return (
                              <Chip
                                key={value}
                                text={Genres[value]}
                                isSelected={isGenreSelected}
                                onClick={() => {
                                  if (isGenreSelected || !value) return (ParamsStore.genre = null);

                                  return (ParamsStore.genre = value);
                                }}
                              />
                            );
                          })}
                        </Content>
                      </ScrollLock>
                    </AccordionItem>
                  )}
                  {showLocationFilter && (
                    <AccordionItem
                      value="location"
                      className="mb-5 data-[state=closed]:animate-closeAccordion data-[state=open]:animate-openAccordion"
                    >
                      <Trigger className="group flex w-full items-center justify-between">
                        <div className="mb-2 font-title text-title-m font-medium">
                          Locations
                          {!!locationValue?.value && (
                            <span className="font-title text-title-m font-normal"> (1)</span>
                          )}
                        </div>
                        <div className="transition-transform group-data-[state=open]:rotate-180">
                          <FontAwesomeIcon icon={faAngleDown} />
                        </div>
                      </Trigger>
                      <Content className="flex gap-2">
                        <div className="w-full rounded-[6px] bg-base200 [&>*]:h-full [&>*]:w-full">
                          {locationSelect}
                        </div>
                      </Content>
                    </AccordionItem>
                  )}
                </Accordion>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </div>
    </Drawer.Root>
  );
};
